import { useState, useEffect } from 'react';
import { industryLabel } from '@/lib/adminDisplayLabels';
import { trackAdminAction } from '@/lib/analytics';
import { Plus, Trash2, Settings, Palette, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AdminDataTable, Column, Action, BulkAction } from '@/components/settings/AdminDataTable';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { PermissionGate } from '@/components/settings/PermissionGate';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CallsignsField } from '@/components/settings/CallsignsField';
import { useRBAC } from '@/contexts/RBACContext';
import { callsignProblem, formatCallsigns, parseCallsigns } from '@/lib/callsigns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getAdminCompanys,
  createAdminCompany,
  updateAdminCompany,
  deleteAdminCompany,
  getFilterIndustries,
} from '@/lib/adminApi';
import { toast } from 'react-toastify';
import { BulkRateAssignmentModal } from '@/components/settings/BulkRateAssignmentModal';
import { AutoRateToggle } from '@/components/settings/AutoRateToggle';
import { EntityRateAssignmentSection } from '@/components/settings/EntityRateAssignmentSection';
import { EntityAuditTimeline } from '@/components/settings/EntityAuditTimeline';
import { AuditLogLink } from '@/components/settings/AuditLogLink';
import { useEnergyRates } from '@/hooks/useEnergyRates';
import { useFilters } from '@/contexts/FilterContext';
import { authenticatedFetch } from '@/lib/api';
import type { BulkRateAssignmentRequest, BulkRateAssignmentResponse } from '@/types/rates';

interface Company {
  id: string;
  name: string;
  slug: string;
  is_internal: boolean;
  industry?: string;
  callsigns?: string[];
  created_at: string;
}

interface Industry {
  id: string;
  name: string;
  slug?: string;
}

export function AdminCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);

  // Bulk rate assignment state
  const [bulkRateModalOpen, setBulkRateModalOpen] = useState(false);
  const [isSubmittingRate, setIsSubmittingRate] = useState(false);
  const { rates, isLoading: isLoadingRates } = useEnergyRates({ autoFetch: true });
  const { refreshFleetData } = useFilters();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    industry: '',
    is_internal: false,
    callsigns: '',
  });
  // Company callsigns are unique across every company, so the server only lets
  // a superadmin set them; everyone else never sends the field.
  const { isSuperadmin } = useRBAC();

  // Bulk edit form state
  const [bulkFormData, setBulkFormData] = useState({
    industry: '__no_change__',
    is_internal: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companiesData, industriesData] = await Promise.all([
        getAdminCompanys(),
        getFilterIndustries(),
      ]);
      setCompanies(companiesData.companies);
      setIndustries(industriesData.industries);
    } catch (error) {
      console.error('Failed to load companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reload after a write that changed company structure.
   *
   * This page reads the uncached /admin API; the Map, filter sidebar and Reports
   * read a cached company list and filter units against an allow-list of selected
   * company ids. Without this, a company created here (and every unit in it) stays
   * invisible in those views until the cache TTL lapses.
   */
  const reloadAfterCompanyMutation = async () => {
    await loadData();
    await refreshFleetData();
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setFormData({ name: '', slug: '', industry: '', is_internal: false, callsigns: '' });
    setDrawerOpen(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      slug: company.slug,
      industry: company.industry || '',
      is_internal: company.is_internal,
      callsigns: formatCallsigns(company.callsigns),
    });
    setDrawerOpen(true);
  };

  const handleDelete = async (company: Company) => {
    if (!confirm(`Are you sure you want to delete company "${company.name}"?`)) {
      return;
    }

    try {
      await deleteAdminCompany(company.id);
      trackAdminAction('company', 'deleted', company.id);
      toast.success('Company deleted successfully');
      reloadAfterCompanyMutation();
    } catch (error: unknown) {
      console.error('Failed to delete company:', error);
      toast.error((error as Error)?.message || 'Failed to delete company');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { callsigns: callsignText, ...fields } = formData;
    const callsigns = parseCallsigns(callsignText);
    if (isSuperadmin) {
      const callsignError = callsignProblem(callsigns);
      if (callsignError) {
        toast.error(callsignError);
        return;
      }
    }
    const payload = isSuperadmin ? { ...fields, callsigns } : fields;

    try {
      if (editingCompany) {
        await updateAdminCompany(editingCompany.id, payload);
        trackAdminAction('company', 'updated', editingCompany.id);
        toast.success('Company updated successfully');
      } else {
        await createAdminCompany(payload);
        trackAdminAction('company', 'created');
        toast.success('Company created successfully');
      }
      setDrawerOpen(false);
      reloadAfterCompanyMutation();
    } catch (error: unknown) {
      console.error('Failed to save company:', error);
      toast.error((error as Error)?.message || 'Failed to save company');
    }
  };

  const columns: Column<Company>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (company) => (
        <button
          onClick={() => navigate(`/settings/company?company=${company.slug}`)}
          className="text-left font-medium text-foreground hover:text-[#8FB560] transition-colors"
        >
          {company.name}
        </button>
      ),
    },
    {
      key: 'slug',
      label: 'Slug',
      sortable: true,
    },
    {
      key: 'industry',
      label: 'Industry',
      render: (company) => industryLabel(company.industry, industries),
    },
    {
      key: 'is_internal',
      label: 'Type',
      render: (company) => (
        <span
          className={`text-xs px-2 py-1 rounded ${
            company.is_internal
              ? 'bg-purple-500 bg-opacity-20 text-purple-600 dark:text-purple-400'
              : 'bg-muted text-foreground'
          }`}
        >
          {company.is_internal ? 'Internal' : 'External'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (company) => new Date(company.created_at).toLocaleDateString(),
    },
  ];

  const actions: Action<Company>[] = [
    {
      label: 'Manage Branding',
      onClick: (company) => navigate(`/settings/company?company=${company.slug}`),
      icon: Palette,
    },
    {
      label: 'Edit',
      onClick: handleEdit,
    },
    {
      label: 'Delete',
      onClick: handleDelete,
      variant: 'destructive',
    },
  ];

  const handleBulkEdit = (companies: Company[]) => {
    setSelectedCompanies(companies);
    setBulkFormData({
      industry: '',
      is_internal: false,
    });
    setBulkDrawerOpen(true);
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const updates: { industry?: string; is_internal?: boolean } = {};
      
      // Only include fields that are set
      if (bulkFormData.industry && bulkFormData.industry !== '__no_change__') {
        updates.industry = bulkFormData.industry;
      }
      updates.is_internal = bulkFormData.is_internal;

      await Promise.all(
        selectedCompanies.map(company => updateAdminCompany(company.id, updates))
      );

      toast.success(`Updated companies`);
      setBulkDrawerOpen(false);
      reloadAfterCompanyMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to update companies');
    }
  };

  // Bulk rate assignment handler
  const handleBulkRateAssignment = async (data: BulkRateAssignmentRequest): Promise<BulkRateAssignmentResponse | null> => {
    setIsSubmittingRate(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/rate-assignments/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result: BulkRateAssignmentResponse = await response.json();

      if (result.created > 0) {
        toast.success(`Rate assigned to ${result.created} compan${result.created !== 1 ? 'ies' : 'y'}`);
      }
      if (result.skipped > 0) {
        toast.warning(`${result.skipped} compan${result.skipped !== 1 ? 'ies' : 'y'} skipped (overlapping dates)`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} error${result.errors.length !== 1 ? 's' : ''} occurred`);
      }

      return result;
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to assign rates');
      return null;
    } finally {
      setIsSubmittingRate(false);
    }
  };

  const bulkActions: BulkAction<Company>[] = [
    {
      label: 'Bulk Edit Metadata',
      icon: Settings,
      onClick: handleBulkEdit,
    },
    {
      label: 'Assign Rate',
      icon: DollarSign,
      onClick: (selectedCompanies) => {
        setSelectedCompanies(selectedCompanies);
        setBulkRateModalOpen(true);
      },
      disabled: isLoadingRates || rates.length === 0,
    },
    {
      label: 'Delete Selected',
      icon: Trash2,
      variant: 'destructive',
      onClick: async (selectedCompanies) => {
        if (!confirm(`Delete ${selectedCompanies.length} companies? This action cannot be undone.`)) return;

        try {
          await Promise.all(
            selectedCompanies.map(company => deleteAdminCompany(company.id))
          );
          toast.success(`Deleted companies`);
          reloadAfterCompanyMutation();
        } catch (error: unknown) {
          toast.error((error as Error)?.message || 'Failed to delete companies');
        }
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap">Companies</h1>
            <AuditLogLink resourceTypes={['company']} tooltip="View company audit history" />
          </div>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Manage organizations and client companies</p>
        </div>
        <PermissionGate permission="companies.write">
          <Button onClick={handleCreate} className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            New Company
          </Button>
        </PermissionGate>
      </div>

      {/* Data Table */}
      <AdminDataTable
        data={companies}
        columns={columns}
        actions={actions}
        bulkActions={bulkActions}
        loading={loading}
        emptyMessage="No companies found"
        tableId="companies"
        searchable
      />

      {/* Create/Edit Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingCompany ? 'Edit Company' : 'Create Company'}
        description={
          editingCompany
            ? 'Update company details'
            : 'Add a new organization or company'
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Warner Bros."
              required
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="e.g., warner-bros"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              URL-friendly identifier (lowercase, no spaces)
            </p>
          </div>

          <div>
            <Label htmlFor="industry">Industry</Label>
            <Select
              value={formData.industry}
              onValueChange={(value) => setFormData({ ...formData, industry: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((industry) => (
                  <SelectItem key={industry.id} value={industry.slug || industry.name}>
                    {industry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSuperadmin && (
            <CallsignsField
              id="company-callsigns"
              value={formData.callsigns}
              onChange={(callsigns) => setFormData({ ...formData, callsigns })}
              example="NBCU"
            />
          )}

          <PermissionGate permission="platform.all">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_internal"
                checked={formData.is_internal}
                onChange={(e) =>
                  setFormData({ ...formData, is_internal: e.target.checked })
                }
                className="rounded border-border"
              />
              <Label htmlFor="is_internal" className="font-normal">
                Hollywood Trucks internal company
              </Label>
            </div>
          </PermissionGate>

          {/* Auto-Rate by Location - only show when editing */}
          {editingCompany && (
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <AutoRateToggle
                entityType="company"
                entityId={editingCompany.id}
              />
              <p className="text-xs text-muted-foreground mt-2">
                When enabled, all units in this company will automatically receive energy rates based on their location.
              </p>
            </div>
          )}

          {/* Rate Assignments Section - only show when editing */}
          {editingCompany && (
            <EntityRateAssignmentSection
              entityType="company"
              entityId={editingCompany.id}
              entityName={editingCompany.name}
            />
          )}

          {/* Activity History - only show when editing */}
          {editingCompany && (
            <EntityAuditTimeline
              entityType="company"
              entityId={editingCompany.id}
              entityName={editingCompany.name}
            />
          )}

          <div className="flex gap-2 pt-4">
            <Button type="submit" className="bg-[#8FB560] hover:bg-[#7a9d51]">
              {editingCompany ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDrawerOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Edit Drawer */}
      <AdminFormDrawer
        open={bulkDrawerOpen}
        onClose={() => setBulkDrawerOpen(false)}
        title={`Bulk Edit: ${selectedCompanies.length} Companies`}
        description="Update multiple companies at once"
      >
        <form onSubmit={handleBulkSubmit} className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-600 dark:text-blue-400">
            Editing {selectedCompanies.length} companies. Only filled fields will be updated.
          </div>

          <div>
            <Label htmlFor="bulk_industry">Industry</Label>
            <Select
              value={bulkFormData.industry}
              onValueChange={(value) => setBulkFormData({ ...bulkFormData, industry: value })}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_change__">No change</SelectItem>
                {industries.map((industry) => (
                  <SelectItem key={industry.id} value={industry.slug || industry.name}>
                    {industry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <PermissionGate permission="platform.all">
            <div className="flex items-center space-x-2 bg-card p-3 rounded-lg border border-border">
              <input
                type="checkbox"
                id="bulk_is_internal"
                checked={bulkFormData.is_internal}
                onChange={(e) =>
                  setBulkFormData({ ...bulkFormData, is_internal: e.target.checked })
                }
                className="rounded border-border"
              />
              <Label htmlFor="bulk_is_internal" className="font-normal cursor-pointer">
                Mark as Hollywood Trucks internal companies
              </Label>
            </div>
          </PermissionGate>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-xs text-amber-600 dark:text-yellow-400">
            This will update {selectedCompanies.length} companies. Empty fields will not be changed.
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              Update {selectedCompanies.length} Companies
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkDrawerOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Rate Assignment Modal */}
      <BulkRateAssignmentModal
        open={bulkRateModalOpen}
        onClose={() => {
          setBulkRateModalOpen(false);
          setSelectedCompanies([]);
        }}
        onSubmit={handleBulkRateAssignment}
        entityType="company"
        selectedEntities={selectedCompanies.map(c => ({
          id: c.id,
          name: c.name,
          currentRate: undefined,
        }))}
        rates={rates}
        isSubmitting={isSubmittingRate}
      />
    </div>
  );
}
